import React, { useState } from "react";
import { Box, IconButton } from "@mui/material";
import colors from "../shared/themes/colors";
import OverflowTooltip from "../shared/components/OverflowTooltip";
import PaperWithThreeStripsIcon from "../shared/assets/icons/PaperWithThreeStripsIcon";
import CloseIcon from "@mui/icons-material/Close";

import DownloadIcon from "../shared/assets/icons/DownloadIcon";
import { formatFileSize } from "../shared/models/models";
import { ServiceReportFile } from "../public/models/serviceReportsModels";
import ImageBox from "./components/ImageBox";
import FullScreenImageDialog from "./components/FullScreenImageDialog";

export interface ReportFileModel {
  name: string;
  content: string;
  size: number;
}

export interface ServiceReportFileProps {
  file: ReportFileModel;
  fileIndex: number;
  onDelete?: (index: number) => void;
  canDelete?: boolean;
  canDownload?: boolean;
  isNew?: boolean;
}

const ReportFile = ({
  file,
  onDelete,
  canDelete = true,
  canDownload = true,
  fileIndex,
  isNew
}: ServiceReportFileProps) => {
  const download = (file: ReportFileModel) => {
    const link = document.createElement("a");
    link.href = file.content;
    link.target = "_blank";
    link.click();
    link.remove();
  };

  const isImageCreateFile =
    file.content.startsWith("data:image/png") ||
    file.content.startsWith("data:image/jpeg") ||
    file.content.startsWith("data:image/jpg");

  const isImageEditFile =
    file.content.toLowerCase().endsWith(".png") ||
    file.content.toLowerCase().endsWith(".jpeg") ||
    file.content.toLowerCase().endsWith(".jpg");

  const isImage = isNew ? isImageCreateFile : isImageEditFile;
  const [isFullScreenImage, setOpenFullScreenImage] = useState(false);

  return (
    <Box
      className="flex justify-between items-center gap-2 w-full py-3 px-4"
      sx={{ background: colors.secondary.gray96, borderRadius: "4px" }}
    >
      <Box className="flex items-center gap-2 flex-1 min-w-0">
        {isImage ? (
          <Box className="cursor-pointer flex-shrink-0" onClick={() => setOpenFullScreenImage(true)}>
            <ImageBox src={file.content} customSize={64} />
          </Box>
        ) : (
          <Box className="flex-shrink-0">
            <PaperWithThreeStripsIcon />
          </Box>
        )}
        <Box className="flex-1 min-w-0">
          <OverflowTooltip text={file.name} className="text-16-medium" color={"#333333"} />
        </Box>
      </Box>

      <Box className="flex gap-2 items-center flex-shrink-0">
        <Box
          className="text-16-medium"
          color="#7A8093"
          sx={{
            minWidth: "max-content"
          }}
        >
          {formatFileSize(file.size || 0)}
        </Box>

        {canDownload && (
          <IconButton onClick={() => download(file)}>
            <DownloadIcon />
          </IconButton>
        )}

        {canDelete && (
          <IconButton onClick={() => onDelete?.(fileIndex)}>
            <CloseIcon sx={{ color: colors.primary.red, fontSize: "16px" }} />
          </IconButton>
        )}
      </Box>

      <FullScreenImageDialog
        open={isFullScreenImage}
        onClose={() => setOpenFullScreenImage(false)}
        image={file.content}
      />
    </Box>
  );
};
export default ReportFile;